<template>
  <div class="roles-tab">

    <!-- Header -->
    <div class="tab-header">
      <div>
        <h2 class="tab-title">Cargos</h2>
        <p class="tab-sub">{{ roles.length }} cargo{{ roles.length !== 1 ? 's' : '' }} cadastrado{{ roles.length !== 1 ? 's' : '' }}</p>
      </div>
      <button v-if="!isAnalyst && !showRoleForm" class="btn-new" type="button" @click="$emit('open-role-form')">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Novo cargo
      </button>
    </div>

    <!-- Role cards list -->
    <div v-if="!showRoleForm">
      <div v-if="roles.length === 0" class="empty-state">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.3"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
        <p>Nenhum cargo cadastrado.<br/>Crie o primeiro cargo para organizar sua equipe.</p>
      </div>
      <div v-else class="roles-list">
        <div v-for="role in roles" :key="role.id" class="role-card">
          <div class="role-card-left">
            <div class="role-name-row">
              <span class="role-name">{{ role.name }}</span>
              <span v-if="role.isAnalyst" class="badge badge--analyst">Analista</span>
              <span v-else-if="role.isAdmin" class="badge badge--admin">Admin</span>
            </div>
            <div class="role-perms">
              <span class="perm-chip" :class="warehouseChipClass(role.warehouseAccess)">
                {{ warehouseShortLabel(role.warehouseAccess) }}
              </span>
              <template v-if="!role.isAnalyst && !role.isAdmin">
                <span v-if="role.constructionAccess" class="perm-chip perm-chip--green">Construção</span>
                <span v-if="role.maintenanceAccess" class="perm-chip perm-chip--orange">Manutenção</span>
                <span v-if="role.portariaAccess" class="perm-chip perm-chip--blue">Portaria</span>
              </template>
              <template v-if="!role.isAnalyst">
                <span v-if="roleSubordinateNames(role).length" class="perm-chip perm-chip--neutral">
                  {{ roleSubordinateNames(role).length }} subordinado{{ roleSubordinateNames(role).length > 1 ? 's' : '' }}
                </span>
              </template>
            </div>
          </div>
          <button v-if="!isAnalyst" class="btn-edit" type="button" @click="$emit('edit-role', role)">Editar</button>
        </div>
      </div>
    </div>

    <!-- Form -->
    <div v-if="showRoleForm && !isAnalyst" class="role-form">
      <div class="form-title">{{ roleForm.isEditing ? 'Editar cargo' : 'Novo cargo' }}</div>

      <!-- Analyst toggle — top, since it overrides everything -->
      <div class="analyst-block" :class="{ 'analyst-block--active': roleForm.isAnalyst }">
        <label class="analyst-toggle">
          <input type="checkbox" :checked="roleForm.isAnalyst"
            @change="updateField('isAnalyst', ($event.target as HTMLInputElement).checked)" />
          <div class="analyst-text">
            <strong>Perfil de analista</strong>
            <span>Acesso completo de leitura — visualiza tudo como admin, mas não cria, edita ou exclui nada.</span>
          </div>
        </label>
        <div v-if="roleForm.isAnalyst" class="analyst-note">
          Quando ativo, todas as outras permissões são ignoradas.
        </div>
      </div>

      <!-- Name -->
      <div class="field">
        <label class="field-label" for="role-name">Nome do cargo</label>
        <input id="role-name" class="field-input" type="text" placeholder="Ex: Encarregado, Pedreiro..."
          :value="roleForm.name"
          @input="updateField('name', ($event.target as HTMLInputElement).value)" />
      </div>

      <!-- Permissions (frozen when analyst) -->
      <fieldset class="perms-block" :disabled="roleForm.isAnalyst" :class="{ frozen: roleForm.isAnalyst }">
        <legend class="perms-legend">Permissões</legend>

        <!-- Admin -->
        <label class="toggle-row">
          <input type="checkbox" :checked="roleForm.isAdmin" :disabled="roleForm.isAnalyst"
            @change="updateField('isAdmin', ($event.target as HTMLInputElement).checked)" />
          <div class="toggle-text">
            <strong>Administrador da obra</strong>
            <span>Pode gerenciar projetos, usuários e configurações.</span>
          </div>
        </label>

        <!-- Warehouse -->
        <div class="field field--no-top">
          <label class="field-label">Acesso ao almoxarifado</label>
          <div class="warehouse-options">
            <label v-for="opt in warehouseOptions" :key="opt.value" class="radio-option"
              :class="{ selected: roleForm.warehouseAccess === opt.value }">
              <input type="radio" :value="opt.value" :checked="roleForm.warehouseAccess === opt.value" :disabled="roleForm.isAnalyst"
                @change="updateField('warehouseAccess', opt.value)" />
              <span class="radio-label">{{ opt.label }}</span>
            </label>
          </div>
        </div>

        <!-- Construction mode -->
        <label class="toggle-row">
          <input type="checkbox" :checked="Boolean(roleForm.constructionAccess)" :disabled="roleForm.isAnalyst"
            @change="updateField('constructionAccess', ($event.target as HTMLInputElement).checked ? 1 : 0)" />
          <div class="toggle-text">
            <strong>Acesso ao Modo Construção</strong>
            <span>Pode acessar projetos, relatórios e o planejamento da obra.</span>
          </div>
        </label>

        <!-- Maintenance mode -->
        <label class="toggle-row">
          <input type="checkbox" :checked="Boolean(roleForm.maintenanceAccess)" :disabled="roleForm.isAnalyst"
            @change="updateField('maintenanceAccess', ($event.target as HTMLInputElement).checked ? 1 : 0)" />
          <div class="toggle-text">
            <strong>Acesso ao Modo Manutenção</strong>
            <span>Pode acessar rotinas e tarefas de manutenção (requer que o módulo esteja ativado no sistema).</span>
          </div>
        </label>

        <!-- Portaria -->
        <div class="field field--no-top">
          <label class="field-label">Acesso à Portaria</label>
          <div class="warehouse-options">
            <label v-for="opt in portariaOptions" :key="opt.value" class="radio-option"
              :class="{ selected: roleForm.portariaAccess === opt.value }">
              <input type="radio" :value="opt.value" :checked="roleForm.portariaAccess === opt.value" :disabled="roleForm.isAnalyst"
                @change="updateField('portariaAccess', opt.value)" />
              <span class="radio-label">{{ opt.label }}</span>
            </label>
          </div>
        </div>

        <!-- Subordinate roles -->
        <div class="field field--no-top">
          <label class="field-label">Cargos subordinados</label>
          <p class="field-hint">Usuários com este cargo podem visualizar/gerenciar usuários dos cargos abaixo.</p>
          <div class="subordinate-tags">
            <span
              v-for="(roleId, idx) in filledSubordinates"
              :key="roleId"
              class="sub-tag"
            >
              {{ roleName(roleId) }}
              <button class="sub-tag-remove" type="button" :disabled="roleForm.isAnalyst"
                @click="removeSubordinate(idx)">×</button>
            </span>
            <select v-if="availableSubordinates.length" class="sub-add-select" :disabled="roleForm.isAnalyst"
              value="" @change="addSubordinate(($event.target as HTMLSelectElement).value); ($event.target as HTMLSelectElement).value = ''">
              <option value="" disabled>+ Adicionar subordinado</option>
              <option v-for="r in availableSubordinates" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <span v-else-if="filledSubordinates.length === 0" class="no-subs">Nenhum subordinado definido</span>
          </div>
        </div>
      </fieldset>

      <p v-if="roleError" class="form-error">{{ roleError }}</p>

      <div class="form-actions">
        <button class="btn-save" type="button" @click="$emit('save-role')">
          {{ roleForm.isEditing ? 'Salvar alterações' : 'Criar cargo' }}
        </button>
        <button class="btn-cancel" type="button" @click="$emit('close-role-form')">Cancelar</button>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import type { Role } from '@/store/roles'

interface RoleFormModel {
  id: string
  name: string
  isAdmin: boolean
  isAnalyst: boolean
  subordinateRoleIds: string[]
  warehouseAccess: number
  maintenanceAccess: number
  constructionAccess: number
  portariaAccess: number
  isEditing: boolean
}

export default defineComponent({
  name: 'UsersRolesTab',
  props: {
    roles: { type: Array as PropType<Role[]>, default: () => [] },
    showRoleForm: { type: Boolean, default: false },
    roleForm: { type: Object as PropType<RoleFormModel>, required: true },
    roleError: { type: String, default: '' },
    isAnalyst: { type: Boolean, default: false }
  },
  emits: [
    'refresh-roles', 'open-role-form', 'edit-role', 'save-role', 'close-role-form',
    'add-role-subordinate', 'remove-role-subordinate', 'update-role-form'
  ],
  setup(props, { emit }) {
    const warehouseOptions = [
      { value: 0, label: 'Sem acesso' },
      { value: 1, label: 'Solicitar itens' },
      { value: 2, label: 'Trabalha no almoxarifado' },
      { value: 3, label: 'Gerencia o almoxarifado' }
    ]

    const portariaOptions = [
      { value: 0, label: 'Sem acesso' },
      { value: 1, label: 'Operar portaria' },
      { value: 2, label: 'Operar + liberar visitantes' },
      { value: 3, label: 'Gestão completa' }
    ]

    const updateField = (field: string, value: string | boolean | number) =>
      emit('update-role-form', { field, value })

    const roleSubordinateNames = (role: Role) =>
      (role.subordinateRoleIds || [])
        .map(id => props.roles.find(r => r.id === id)?.name || '')
        .filter(Boolean)

    const roleName = (id: string) =>
      props.roles.find(r => r.id === id)?.name || id

    // Only the non-empty entries
    const filledSubordinates = computed(() =>
      (props.roleForm.subordinateRoleIds || []).filter(Boolean)
    )

    // Roles not yet selected and not the role being edited
    const availableSubordinates = computed(() =>
      props.roles.filter(r => r.id !== props.roleForm.id && !filledSubordinates.value.includes(r.id))
    )

    const addSubordinate = (roleId: string) => {
      if (!roleId) return
      // Find the first empty slot in the array
      const ids = props.roleForm.subordinateRoleIds
      const emptyIdx = ids.findIndex(v => !v)
      if (emptyIdx >= 0) {
        emit('update-role-form', { field: 'subordinateRoleIds', index: emptyIdx, value: roleId })
      } else {
        emit('add-role-subordinate')
        // After parent adds an empty slot, update it — use a microtask to let the prop update
        setTimeout(() => {
          const newIds = props.roleForm.subordinateRoleIds
          const newEmpty = newIds.findIndex(v => !v)
          if (newEmpty >= 0) emit('update-role-form', { field: 'subordinateRoleIds', index: newEmpty, value: roleId })
        }, 0)
      }
    }

    const removeSubordinate = (filledIndex: number) => {
      // Map back to the full array index
      const ids = props.roleForm.subordinateRoleIds
      let count = 0
      for (let i = 0; i < ids.length; i++) {
        if (ids[i]) {
          if (count === filledIndex) { emit('remove-role-subordinate', i); return }
          count++
        }
      }
    }

    const warehouseShortLabel = (value: number) => {
      if (value === 1) return 'Solicita itens'
      if (value === 2) return 'Almoxarife'
      if (value === 3) return 'Gestor almox.'
      return 'Sem almoxarifado'
    }

    const warehouseChipClass = (value: number) => {
      if (value === 0) return 'perm-chip--muted'
      if (value === 1) return 'perm-chip--blue'
      if (value === 2) return 'perm-chip--teal'
      return 'perm-chip--purple'
    }

    return {
      warehouseOptions,
      portariaOptions,
      updateField,
      roleSubordinateNames,
      roleName,
      filledSubordinates,
      availableSubordinates,
      addSubordinate,
      removeSubordinate,
      warehouseShortLabel,
      warehouseChipClass
    }
  }
})
</script>

<style scoped lang="scss">
.roles-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Header ── */
.tab-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.tab-title {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink, #201f1e);
}

.tab-sub {
  margin: 0;
  font-size: 12px;
  color: var(--ink-muted, #888);
}

.btn-new {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--accent, #0f4c81);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;

  &:hover { opacity: 0.88; }
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 20px;
  text-align: center;
  color: var(--ink-muted, #888);
  font-size: 13px;
  line-height: 1.6;

  p { margin: 0; }
}

/* ── Role cards ── */
.roles-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: var(--card, #fff);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 10px;
  transition: box-shadow 0.15s;

  &:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.06); }
}

.role-card-left {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.role-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.role-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink, #201f1e);
}

.badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;

  &--analyst { background: #fef9c3; color: #854d0e; }
  &--admin { background: #dbeafe; color: #1d4ed8; }
}

.role-perms {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.perm-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;

  &--muted { background: rgba(0,0,0,0.06); color: var(--ink-muted, #888); }
  &--blue { background: #dbeafe; color: #1d4ed8; }
  &--teal { background: #ccfbf1; color: #0f766e; }
  &--purple { background: #ede9fe; color: #7c3aed; }
  &--neutral { background: rgba(0,0,0,0.06); color: var(--ink-muted); }
  &--green { background: #dcfce7; color: #15803d; }
  &--orange { background: #fff7ed; color: #c2410c; }
}

.btn-edit {
  padding: 6px 14px;
  background: transparent;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink, #201f1e);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s;

  &:hover { background: rgba(0,0,0,0.04); }
}

/* ── Form ── */
.role-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--card, #fff);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 12px;
  padding: 22px;
}

.form-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink, #201f1e);
  margin-bottom: 4px;
}

/* Analyst block */
.analyst-block {
  background: rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  padding: 14px 16px;
  transition: background 0.2s, border-color 0.2s;

  &--active {
    background: #fefce8;
    border-color: #fbbf24;
  }
}

.analyst-toggle {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;

  input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; }
}

.analyst-text {
  display: flex;
  flex-direction: column;
  gap: 3px;

  strong { font-size: 13px; color: var(--ink, #201f1e); }
  span { font-size: 12px; color: var(--ink-muted, #888); }
}

.analyst-note {
  margin-top: 10px;
  font-size: 12px;
  color: #92400e;
  font-weight: 500;
}

/* Fields */
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;

  &--no-top { padding-top: 0; }
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted, #888);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-hint {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--ink-muted, #888);
}

.field-input {
  padding: 9px 12px;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 7px;
  font-size: 13px;
  color: var(--ink, #201f1e);
  background: #fff;
  transition: border-color 0.15s;

  &:focus { outline: none; border-color: var(--accent, #0f4c81); }
}

/* Permissions fieldset */
.perms-block {
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
}

.perms-legend {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-muted, #888);
  padding: 0 6px;
}

.toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;

  input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; }
}

.toggle-text {
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong { font-size: 13px; color: var(--ink, #201f1e); }
  span { font-size: 12px; color: var(--ink-muted, #888); }
}

/* Warehouse radio options */
.warehouse-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &.selected {
    background: rgba(15, 76, 129, 0.06);
    border-color: var(--accent, #0f4c81);
  }

  input[type="radio"] { flex-shrink: 0; }
}

.radio-label {
  font-size: 13px;
  color: var(--ink, #201f1e);
}

/* Subordinate tags */
.subordinate-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.sub-tag {
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(15, 76, 129, 0.08);
  color: var(--accent, #0f4c81);
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.sub-tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: inherit;
  opacity: 0.6;
  padding: 0;

  &:hover { opacity: 1; }
  &:disabled { cursor: not-allowed; }
}

.sub-add-select {
  padding: 5px 10px;
  border: 1px dashed rgba(0,0,0,0.2);
  border-radius: 20px;
  font-size: 12px;
  color: var(--ink-muted, #888);
  background: transparent;
  cursor: pointer;

  &:focus { outline: none; border-color: var(--accent, #0f4c81); }
  &:disabled { cursor: not-allowed; }
}

.no-subs {
  font-size: 12px;
  color: var(--ink-muted, #888);
  font-style: italic;
}

/* Error */
.form-error {
  font-size: 13px;
  color: #b91c1c;
  margin: 0;
  background: #fee2e2;
  padding: 8px 12px;
  border-radius: 6px;
}

/* Actions */
.form-actions {
  display: flex;
  gap: 10px;
  padding-top: 4px;
}

.btn-save {
  padding: 10px 22px;
  background: var(--accent, #0f4c81);
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover { opacity: 0.88; }
}

.btn-cancel {
  padding: 10px 18px;
  background: transparent;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #201f1e);
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: rgba(0,0,0,0.04); }
}

.frozen {
  opacity: 0.4;
  pointer-events: none;
}
</style>
